import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getNodeSupport } from '@atlassian/ai-model-io/utils/get-node-support';
import type { BaseAIAction } from '@atlassian/ai-smart-button-api';

import type { AIPlugin } from '../../../aiPluginType';
import type { GetMentionNameDetails, AISmartButtonOptions } from '../../../types/types';

import {
	FIRST_NUDGE_CHECK_DURATION,
	NUDGE_AFTER_DOC_CHANGE_DURATION,
	NUDGE_DURATION,
} from './constants';
import { canNudgeFullPageImproveWriting } from './request';

/**
 * Used `nudger` noun to name the interface as it is the one who nudges.
 * `nudger` dictionary meaning: someone who nudges
 */
export interface SmartButtonNudger {
	start(): void;
	stop(): void;
	setLastDocChangeTime(time: number): void;
	isActive(): boolean;
}

type SmartButtonNudgerOptions = {
	locale: string;
	getNudge: (confidenceScore?: number) => BaseAIAction;
	proactiveAIApiUrl: string;
	product: string;
	view: EditorView;
	// Optional options
	api?: ExtractInjectionAPI<AIPlugin>;
	getMentionNameDetails?: GetMentionNameDetails;
};

export class FullPageImproveWritingNudger implements SmartButtonNudger {
	private timerId?: ReturnType<typeof setTimeout>;
	private active: boolean = false;
	private lastDocChangeTime: number = 0;

	constructor(
		private aiSmartButtonOptions: AISmartButtonOptions,
		private nudgerOptions: SmartButtonNudgerOptions,
	) {
		this.lastDocChangeTime = Date.now();
	}

	setLastDocChangeTime(time: number): void {
		this.lastDocChangeTime = time;
	}

	isActive(): boolean {
		return this.active;
	}

	private async checkForNudge() {
		if (!this.active || !this.aiSmartButtonOptions || !this.nudgerOptions) {
			return;
		}

		/**
		 * Wait NUDGE_AFTER_DOC_CHANGE_DURATION milliseconds after the last document change before nudging.
		 * When user is contantly typing and updating the document, we don't want to nudge the user too frequently.
		 * We want to wait for a pause in typing before nudging.
		 * In future we will employ a more sophisticated algorithm to determine when to nudge.
		 */
		const currentTimeStamp = Date.now();
		if (currentTimeStamp < this.lastDocChangeTime + NUDGE_AFTER_DOC_CHANGE_DURATION) {
			/**
			 * Check again after the remaining time, if user types again,
			 * 	will keep waiting till user stops typing for NUDGE_AFTER_DOC_CHANGE_DURATION
			 */
			this.next(this.lastDocChangeTime + NUDGE_AFTER_DOC_CHANGE_DURATION - currentTimeStamp);
			return;
		}

		/**
		 * If there are any unresolved comments in the document, then we don't want to nudge.
		 */
		const hasUnresolvedComments =
			this.nudgerOptions.api?.annotation?.actions.hasAnyUnResolvedAnnotationInPage(
				this.nudgerOptions.view.state,
			);
		if (hasUnresolvedComments) {
			// Author can delete comments, so we will check again after NUDGE_DURATION (2 minutes)
			this.next();
			return;
		}

		// Check if the document has only nodes supported by MD/MD+.
		const supportedNodes = new Set(
			Object.entries(getNodeSupport())
				.filter(([, value]) => value === 'full' || value === 'custom')
				.map(([key]) => key),
		);
		supportedNodes.add('doc');
		if (!this.hasOnlySupportedNodes(this.nudgerOptions.view.state.doc, supportedNodes)) {
			this.next();
			return;
		}

		const { canNudge, confidenceScore } = await canNudgeFullPageImproveWriting({
			proactiveAIApiUrl: this.nudgerOptions.proactiveAIApiUrl,
			product: this.nudgerOptions.product,
			context: {
				view: this.nudgerOptions.view,
				getMentionNameDetails: this.nudgerOptions.getMentionNameDetails,
				locale: this.nudgerOptions.locale,
			},
		});

		if (canNudge) {
			const nudge = this.nudgerOptions.getNudge(confidenceScore);
			this.aiSmartButtonOptions.actions.setNudges([nudge]);
			// Once we add a nudge, need to stop, because we nudge only once per editing session.
			this.stop();
		} else {
			this.next();
		}
	}

	private next(duration = NUDGE_DURATION) {
		if (this.timerId) {
			clearTimeout(this.timerId);
		}
		this.timerId = setTimeout(this.checkForNudge.bind(this), duration);
	}

	start() {
		if (!this.active) {
			this.active = true;
			this.next(FIRST_NUDGE_CHECK_DURATION);
		}
	}

	stop() {
		this.active = false;
		clearTimeout(this.timerId);
		this.timerId = undefined;
	}

	private hasOnlySupportedNodes(node: Node, supportedNodes: Set<string>): boolean {
		const isNodeTypeSupported = supportedNodes.has(node.type.name);
		if (!isNodeTypeSupported && node.attrs) {
			let isValid = false;
			switch (node.type.name) {
				case 'table':
					const hasCorrectLayout = node.attrs.layout === 'default';
					isValid =
						hasCorrectLayout &&
						this.areRemainingAttrsFalsey(node.attrs, ['layout', 'localId', 'width']);
					break;

				case 'tableHeader':
				case 'tableRow':
				case 'tableCell':
					const hasValidColspan = node.attrs.colspan === undefined || node.attrs.colspan === 1;
					const hasValidRowspan = node.attrs.rowspan === undefined || node.attrs.rowspan === 1;
					isValid =
						hasValidColspan &&
						hasValidRowspan &&
						this.areRemainingAttrsFalsey(node.attrs, ['colspan', 'rowspan']);
					break;

				case 'mediaSingle':
					const hasMediaCorrectLayout = node.attrs.layout === 'center';
					const hasCorrectWidthType = !node.attrs.widthType || node.attrs.widthType === 'pixel';
					isValid =
						hasMediaCorrectLayout &&
						hasCorrectWidthType &&
						this.areRemainingAttrsFalsey(node.attrs, ['layout', 'widthType', 'width']);
					break;

				default:
					return false; // Node type not supported and not a special case.
			}

			if (!isValid) {
				return false;
			}
		}

		for (let i = 0; i < node.childCount; i++) {
			if (!this.hasOnlySupportedNodes(node.child(i), supportedNodes)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Checks if all attributes not in the ignore list have falsey values
	 */
	private areRemainingAttrsFalsey(attrs: Record<string, unknown>, ignoreAttrs: string[]): boolean {
		const remainingAttrs = Object.keys(attrs).filter((attr) => !ignoreAttrs.includes(attr));

		return remainingAttrs.every((attr) => !attrs[attr]);
	}
}
